import React from 'react';
import {
  Button,
  Dialog,
  Grid,
  MenuItem,
  Select,
  theme,
} from '@klover/attain-design-system';
import {
  ToggleDuplicateOfferDialogAction,
  checkDuplicateTuneOfferId,
  duplicateOffer,
  fetchGroups,
  setTuneOfferIdError,
  toggleDuplicateOfferDialog,
} from '../slice';
import { useAppDispatch, useAppSelector } from 'redux/hooks';

import Field from 'components/Field';
import GhostLoader from 'react-ghost-loader';
import TextField from '@material-ui/core/TextField';
import { DuplicateOfferError } from '../offerDimensionDialog';
import { Formik, useFormik } from 'formik';
import { OFFER_FIELD_MAPPING } from 'constants/index';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { isValidUrl } from '../../../utils/isValidUrl';
import * as Styled from './index.styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3),
    },
    selectGroup: {
      width: '70%',
      height: '50px',
      '& fieldset': {
        top: '-12px !important',
      },
    },
    textInput: {
      paddingBottom: 5,
      width: '70%',
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
      },
    },
  })
);

interface Props {
  open: boolean;
}

const DuplicateOfferDialog = ({ open }: Props) => {
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const offerToDuplicate = useAppSelector(
    (state) => state.offerWallReducer.currentOffer
  );
  const formik = useFormik({
    initialValues: {
      offerGroupId: '',
      groupName: '',
      ctaurl: '',
    },
    onSubmit: (values) => {
      dispatch(
        duplicateOffer({
          ...values,
          offerToDuplicate,
        })
      );
    },
  });

  const groupsEntities = useAppSelector((state) => {
    if (
      state.offerWallReducer &&
      state.offerWallReducer.entities &&
      state.offerWallReducer.entities.groups
    ) {
      return state.offerWallReducer.entities.groups;
    } else {
      return null;
    }
  });

  const groupIds = groupsEntities ? groupsEntities.allIds : null;

  const dispatchClose = () => {
    const actionProps: ToggleDuplicateOfferDialogAction = {
      open: false,
    };
    dispatch(toggleDuplicateOfferDialog(actionProps));
  };

  const offerLoading = useAppSelector(
    (state) => state.offerWallReducer.ui.offerLoading
  );

  const isOfferLoading = offerLoading.state === 'loading';

  const tuneOfferIdError = useAppSelector(
    (state) => state.offerWallReducer.ui.tuneOfferIdError
  );

  const duplicateOfferIds = useAppSelector(
    (state) => state.offerWallReducer.ui.duplicateOfferIds
  );

  const isTuneGoalIdValidating = useAppSelector(
    (state) => state.offerWallReducer.ui.isTuneGoalIdValidating
  );

  const handleClose = () => {
    dispatchClose();
    // Reset validation state when closing
    dispatch(setTuneOfferIdError({ message: null, duplicateOfferIds: [] }));
    formik.resetForm();
  };

  const handleCtaUrlBlur = (value: string) => {
    const trimmedValue = value.trim();
    formik.setFieldValue('ctaurl', trimmedValue);

    dispatch(setTuneOfferIdError({ message: null, duplicateOfferIds: [] }));

    if (!trimmedValue) {
      return;
    }

    const urlValidation = isValidUrl(
      trimmedValue,
      OFFER_FIELD_MAPPING.clickthrough_url,
      ['offer_id']
    );

    if (!urlValidation.isValid) {
      dispatch(
        setTuneOfferIdError({
          message: urlValidation.message,
          duplicateOfferIds: [],
        })
      );
    } else {
      // Create a temporary offer object for validation
      // Use empty offerid since this is a new offer being duplicated
      const tempOffer = {
        ...offerToDuplicate,
        ctaurl: trimmedValue,
        tuneofferid: urlValidation.params.offer_id,
        offerid: '', // Empty since this is a new offer
      };
      // Check for duplicate tune offer ID
      dispatch(checkDuplicateTuneOfferId(tempOffer));
    }
  };

  // Check if validation passed (no error and not validating and ctaurl is provided)
  const isCtaUrlValid =
    formik.values.ctaurl?.trim() &&
    !tuneOfferIdError &&
    !isTuneGoalIdValidating;

  React.useEffect(() => {
    dispatch(fetchGroups());
    // Reset validation state when dialog opens
    if (open) {
      dispatch(setTuneOfferIdError({ message: null, duplicateOfferIds: [] }));
    }
  }, [dispatch, open]);

  return (
    <>
      <Formik
        initialValues={{ alignment: 'oldGroups' }}
        onSubmit={formik.handleSubmit}
      >
        {({ values }) => (
          <Dialog
            fullWidth
            className={classes.root}
            onClose={handleClose}
            open={open}
            headerID="createDuplicateOffer"
            title=""
            header={
              <Styled.CardHeader>
                <Styled.CardTitle>Duplicate Offer</Styled.CardTitle>
              </Styled.CardHeader>
            }
            bodyID="duplicateOffer"
            footer={
              <>
                <Button
                  variant="contained"
                  disabled={
                    (values.alignment == 'newGroup' &&
                      !formik.values.groupName) ||
                    !isCtaUrlValid
                  }
                  onClick={formik.handleSubmit}
                >
                  Duplicate Offer
                </Button>
                <Button
                  variant="outlined"
                  disableElevation
                  disabled={isOfferLoading}
                  onClick={handleClose}
                >
                  Cancel
                </Button>
              </>
            }
            body={
              <Styled.StyledBodyContainer
                container
                spacing={theme.spacing.lg}
                direction="column"
              >
                <Grid item>
                  <p>
                    Are you sure you want to duplicate the offer? Choose an
                    offer group or select a new one.
                  </p>
                  <Styled.RadioGroup
                    role="group"
                    aria-labelledby="my-radio-group"
                  >
                    <div>
                      <label htmlFor="oldGroups">
                        <Field
                          id="oldGroups"
                          type="radio"
                          name="alignment"
                          value="oldGroups"
                        />
                        <span>Select Group</span>
                      </label>
                    </div>
                    <div>
                      <label htmlFor="newGroup">
                        <Field
                          id="newGroup"
                          type="radio"
                          name="alignment"
                          value="newGroup"
                        />
                        <span>Create New Group</span>
                      </label>
                    </div>
                  </Styled.RadioGroup>
                  {values.alignment == 'oldGroups' ? (
                    <Select
                      name="offerGroupId"
                      id="offerGroupId"
                      onChange={formik.handleChange}
                      value={formik.values.offerGroupId || 'default'}
                      className={classes.selectGroup}
                    >
                      <MenuItem key="default" value="default">
                        Default
                      </MenuItem>
                      {groupIds
                        ? groupIds.map((groupId) => {
                            return (
                              <MenuItem key={groupId} value={groupId}>
                                {groupsEntities &&
                                groupsEntities.byId &&
                                groupsEntities.byId[groupId] &&
                                groupsEntities.byId[groupId].name
                                  ? groupsEntities.byId[groupId].name
                                  : null}
                              </MenuItem>
                            );
                          })
                        : null}
                    </Select>
                  ) : (
                    <Field
                      type="text"
                      value={formik.values.groupName}
                      id="groupName"
                      name="groupName"
                      placeholder="e.g. Walmart Offers..."
                      onChange={formik.handleChange}
                      style={{ width: 250 }}
                    />
                  )}
                  <div style={{ marginTop: theme.spacing.md }}>
                    <TextField
                      variant="outlined"
                      label="Clickthrough Url"
                      value={formik.values.ctaurl || ''}
                      onChange={(e) => {
                        const trimmedValue = e.target.value.trim();
                        formik.setFieldValue('ctaurl', trimmedValue);
                        handleCtaUrlBlur(trimmedValue);
                      }}
                      className={classes.textInput}
                      required
                    />
                    {formik.values.ctaurl ? (
                      isTuneGoalIdValidating ? (
                        <GhostLoader
                          height="20px"
                          heightRandom={0}
                          width="100%"
                          widthRandom={0}
                        />
                      ) : (
                        <DuplicateOfferError
                          tuneOfferIdError={tuneOfferIdError}
                          duplicateOfferIds={duplicateOfferIds}
                        />
                      )
                    ) : null}
                  </div>
                </Grid>
              </Styled.StyledBodyContainer>
            }
          ></Dialog>
        )}
      </Formik>
    </>
  );
};

export default DuplicateOfferDialog;
